/// Autopilot helpers for the three air-only goal types the web port
/// supports: landing, transition (overfly an exit fix), and takeoff
/// (ground-roll → V2 → climb out toward an exit fix).
///
/// Direct ports of:
///   - FlatoutATC/Models/GameViewModel.swift:2671-2870 (issueLandingClearance)
///   - FlatoutATC/Support/GameScene.swift:881-926 (touchdown detect)
///   - FlatoutATC/Models/GameViewModel.swift:1612-1716 (transition exit)

import type { Aircraft } from './Aircraft'
import { performanceFor } from './AircraftDatabase'
import type {
  Airport,
  LevelData,
  ProcedureLeg,
  Runway,
} from '../types/LevelData'
import type { CoordinateProjector } from '../geo/CoordinateProjector'

const FT_PER_NM = 6076.115
/// iOS default for `GameSettings.allowedHeadingOffset` — see
/// FlatoutATC/Support/GameSettings.swift:24 (10°). The aircraft has
/// to be tracking very close to the runway approach heading; the
/// 45° we previously used would silently pass anything within a
/// quadrant.
const MAX_HEADING_OFFSET_DEG = 10
/// "Cleared for approach" is the earlier, looser checkpoint —
/// aircraft just needs to be vaguely pointed at the runway. Once
/// the autopilot pulls it onto the localizer, the tighter
/// landing-clearance gate (10°) takes over.
const APPROACH_INTERCEPT_MAX_OFFSET_DEG = 45
const APPROACH_INTERCEPT_MAX_DISTANCE_NM = 15
const MAX_DESCENT_RATE_FT_PER_MIN = 3000
const TOUCHDOWN_RADIUS_NM = 0.1
const TOUCHDOWN_ALTITUDE_MARGIN_FT = 10
/// Distance at which an aircraft is "over" its exit fix and gets
/// handed off the radar (transition / takeoff goals).
const FIX_OVERFLY_RADIUS_NM = 0.5
/// Default cruise altitude assigned post-takeoff when there's no SID.
const TAKEOFF_TARGET_ALTITUDE_FT = 6000

export type LandingRejection =
  | 'not_aligned'
  | 'off_centerline'
  | 'too_far'
  | 'descent_too_steep'
  | 'speed_zero'
  | 'invalid_runway'

export interface LandingClearanceResult {
  ok: boolean
  reason?: LandingRejection
}

export interface LandingClearanceArgs {
  aircraft: Aircraft
  airport: Airport
  runway: Runway
  /// Which end the aircraft is landing toward — heading1 or heading2.
  approachHeadingDeg: number
  projector: CoordinateProjector
  pxPerNM: number
}

/// Detailed landing-clearance evaluation. Mirrors the four iOS gates
/// (alignment / centerline / proximity / descent rate) plus the
/// speed-zero sanity check, and exposes each metric so the UI can
/// surface "why is the button disabled" without re-deriving them.
export interface LandingEvaluation {
  ok: boolean
  reason?: LandingRejection
  /// Shortest absolute heading delta between aircraft heading and
  /// approach heading, [0, 180].
  headingOffsetDeg: number
  /// Straight-line distance from aircraft to threshold, NM.
  distanceNM: number
  /// Perpendicular offset from the runway extended centerline, NM.
  centerlineOffsetNM: number
  /// Required descent rate to make the threshold at field elevation,
  /// ft/min. Negative when aircraft is below field elevation (rare).
  requiredDescentRateFtPerMin: number
  /// Threshold position in world-space pixels (used by issue() to
  /// drive the autopilot when accepted).
  thresholdPx: { x: number; y: number }
}

const MAX_CENTERLINE_FALLBACK_NM = 1.0

/// Pure evaluation — does not mutate the aircraft. Used by both the
/// issue() entry point and the UI's button-enabled predicate so the
/// two can never disagree.
///
/// Mirrors FlatoutATC/Models/GameViewModel.swift:2671-2870 (every
/// guard becomes a rejection reason here, in the same order).
export function evaluateLandingClearance(args: LandingClearanceArgs): LandingEvaluation {
  const { aircraft, airport, runway, approachHeadingDeg, projector, pxPerNM } = args

  // Threshold: the *near* end of the runway given approach heading.
  // If approach is heading1 we land toward end1 (i.e., the aircraft
  // crosses startCoordinate); else end2 (endCoordinate).
  const thresholdGeo =
    approachHeadingDeg === runway.heading1 ? runway.startCoordinate : runway.endCoordinate
  const thresholdPx = projector.project(thresholdGeo, true)

  const dx = aircraft.position.x - thresholdPx.x
  const dy = aircraft.position.y - thresholdPx.y
  const distPx = Math.hypot(dx, dy)
  const distanceNM = distPx / pxPerNM

  // Heading offset, [0, 180].
  let headingOffsetDeg = Math.abs(aircraft.headingDeg - approachHeadingDeg)
  if (headingOffsetDeg > 180) headingOffsetDeg = 360 - headingOffsetDeg

  // Centerline offset: perpendicular distance from the runway
  // extended-axis line. Aircraft on final should approach from the
  // OPPOSITE side of the threshold to the landing direction, so the
  // expected magnetic bearing from threshold to aircraft is
  // (approachHeading + 180). The lateral offset is dist*sin(angleDelta).
  const bearingFromThreshold = headingToward(thresholdPx, aircraft.position)
  const expectedBearing = (approachHeadingDeg + 180) % 360
  let bearingDeltaDeg = bearingFromThreshold - expectedBearing
  while (bearingDeltaDeg > 180) bearingDeltaDeg -= 360
  while (bearingDeltaDeg < -180) bearingDeltaDeg += 360
  const centerlineOffsetNM =
    Math.abs(distanceNM * Math.sin((bearingDeltaDeg * Math.PI) / 180))

  // Required descent rate to reach field elevation by the threshold,
  // at current ground speed.
  const distFt = distanceNM * FT_PER_NM
  const speedFtPerMin = (aircraft.speedKts * FT_PER_NM) / 60
  const elevation = airport.elevation ?? 0
  const altAboveAirport = aircraft.altitudeFt - elevation
  const timeToRunwayMin = speedFtPerMin > 0 ? distFt / speedFtPerMin : Infinity
  const requiredDescentRateFtPerMin =
    timeToRunwayMin > 0 ? altAboveAirport / timeToRunwayMin : Infinity

  const base = {
    headingOffsetDeg,
    distanceNM,
    centerlineOffsetNM,
    requiredDescentRateFtPerMin,
    thresholdPx,
  }

  // Apply gates in iOS order. Helicopters (which can hover and
  // descend vertically) skip the speed-zero, alignment,
  // centerline, and descent-rate gates — they only need to be
  // close enough to the field. Distance gate still applies.
  const isHelicopter = aircraft.type === 'helicopter'
  if (!isHelicopter) {
    if (aircraft.speedKts <= 0) return { ok: false, reason: 'speed_zero', ...base }
    if (headingOffsetDeg > MAX_HEADING_OFFSET_DEG) {
      return { ok: false, reason: 'not_aligned', ...base }
    }
    const centerLineTolerance = airport.centerLineTolerance ?? MAX_CENTERLINE_FALLBACK_NM
    if (centerlineOffsetNM >= centerLineTolerance) {
      return { ok: false, reason: 'off_centerline', ...base }
    }
  }
  if (distanceNM > airport.landingApproachDistance) {
    return { ok: false, reason: 'too_far', ...base }
  }
  if (!isHelicopter && requiredDescentRateFtPerMin > MAX_DESCENT_RATE_FT_PER_MIN) {
    return { ok: false, reason: 'descent_too_steep', ...base }
  }

  return { ok: true, ...base }
}

/// Try to accept a landing clearance for the given aircraft on the
/// given runway end. Delegates to `evaluateLandingClearance`; on
/// success, mutates the aircraft into `landing` status with the
/// computed descent rate + threshold target.
export function issueLandingClearance(args: LandingClearanceArgs): LandingClearanceResult {
  const evalResult = evaluateLandingClearance(args)
  if (!evalResult.ok) {
    return { ok: false, reason: evalResult.reason }
  }
  const { aircraft, airport, runway, approachHeadingDeg } = args
  const elevation = airport.elevation ?? 0
  aircraft.status = 'landing'
  aircraft.landingRunway = runway
  aircraft.landingApproachHeadingDeg = approachHeadingDeg
  aircraft.descentRateOverride = Math.max(0, evalResult.requiredDescentRateFtPerMin)
  aircraft.targetAltitudeFt = elevation
  aircraft.directToPoint = evalResult.thresholdPx
  // Approach intercept is no longer authoritative — landing autopilot
  // owns heading + descent now.
  aircraft.approachIntercept = null
  // Cleared to land supersedes any active hold.
  aircraft.holdingState = null
  return { ok: true }
}

export interface TouchdownInfo {
  aircraftId: string
  airportName: string
}

/// Per-tick autopilot for landing aircraft. Steers toward the
/// threshold and detects touchdown. Returns a TouchdownInfo when the
/// aircraft has landed this frame.
export function tickLanding(
  aircraft: Aircraft,
  airports: Airport[],
  pxPerNM: number,
  projector?: CoordinateProjector,
): TouchdownInfo | null {
  if (aircraft.status !== 'landing' || !aircraft.directToPoint || !aircraft.landingRunway) {
    return null
  }

  // Re-project the threshold each tick from the live runway
  // endpoint. For static airports this is a no-op (coords don't
  // change); for moving carriers, the cached directToPoint would
  // otherwise drift away from the deck as it sails. Projector
  // optional so the function still works in unit tests that hand-
  // build a `directToPoint` and don't need motion tracking.
  if (projector && aircraft.landingApproachHeadingDeg != null) {
    const r = aircraft.landingRunway
    const thresholdGeo =
      aircraft.landingApproachHeadingDeg === r.heading1
        ? r.startCoordinate
        : r.endCoordinate
    aircraft.directToPoint = projector.project(thresholdGeo, true)
  }

  aircraft.targetHeadingDeg = headingToward(aircraft.position, aircraft.directToPoint)

  // Find the airport for elevation + motion lookup.
  const airport = airports.find((a) =>
    a.runways.some(
      (r) =>
        r.id1 === aircraft.landingRunway?.id1 && r.id2 === aircraft.landingRunway?.id2,
    ),
  )
  const elevation = airport?.elevation ?? 0

  // Recompute the descent rate each tick using the LIVE closure
  // rate. On a static airport that's just the aircraft's GS along
  // its current heading, so the descent rate stays roughly steady.
  // On a moving carrier, closure = (aircraft velocity − carrier
  // velocity) projected onto the direction-to-threshold; for
  // typical opposed-course carrier landings (aircraft 260°, carrier
  // 090° at 30 kt) closure runs ~170 kt vs the aircraft's 140 kt
  // GS, so the aircraft otherwise reaches the threshold ~20%
  // faster than the static-airport descent rate accounts for and
  // still has 100+ ft of altitude when it overflies the deck.
  if (aircraft.altitudeFt > elevation) {
    const dxToThr = aircraft.directToPoint.x - aircraft.position.x
    const dyToThr = aircraft.directToPoint.y - aircraft.position.y
    const distPx = Math.hypot(dxToThr, dyToThr)
    if (distPx > 1) {
      const distNM = distPx / pxPerNM
      const ux = dxToThr / distPx
      const uy = dyToThr / distPx
      // Compass heading → screen-vector (Y-down convention).
      const acRad = (aircraft.headingDeg * Math.PI) / 180
      const acVx = Math.sin(acRad)
      const acVy = -Math.cos(acRad)
      const acAlongKts = (acVx * ux + acVy * uy) * aircraft.speedKts
      let thrAlongKts = 0
      if (airport?.motion) {
        const tRad = (airport.motion.headingDeg * Math.PI) / 180
        const tVx = Math.sin(tRad)
        const tVy = -Math.cos(tRad)
        thrAlongKts = (tVx * ux + tVy * uy) * airport.motion.speedKts
      }
      const closingKts = acAlongKts - thrAlongKts
      if (closingKts > 1) {
        const timeToTouchdownSec = (distNM / closingKts) * 3600
        const altToLose = aircraft.altitudeFt - elevation
        aircraft.descentRateOverride = Math.max(
          0,
          (altToLose / timeToTouchdownSec) * 60,
        )
      }
    }
  }

  // Touchdown: alt ≤ elev + 10 ft AND within 0.1 NM of threshold.
  if (aircraft.altitudeFt <= elevation + TOUCHDOWN_ALTITUDE_MARGIN_FT) {
    const dx = aircraft.directToPoint.x - aircraft.position.x
    const dy = aircraft.directToPoint.y - aircraft.position.y
    const distPx = Math.hypot(dx, dy)
    if (distPx <= TOUCHDOWN_RADIUS_NM * pxPerNM) {
      aircraft.status = 'landed'
      return { aircraftId: aircraft.id, airportName: airport?.name ?? '' }
    }
  }
  return null
}

/// Auto-engage distance for helicopters with a `landHelipad` goal.
/// At this lateral range the autopilot takes over: status flips to
/// `landing`, descent rate clamps to the helo expedited rate, and
/// the aircraft is steered to the pad. Mirrors iOS:1.5 NM.
const HELIPAD_AUTO_ENGAGE_DISTANCE_NM = 1.5
/// Helicopter expedited descent rate in ft/min — used as the
/// override during a helipad landing. iOS reads this from
/// `AircraftDatabase.performanceData[.helicopter].expeditedDescentRate`
/// (1000); web's perf table doesn't carry an "expedited" field, so
/// hold the value here.
const HELIPAD_DESCENT_RATE_FT_PER_MIN = 1000

/// Per-tick autopilot for helicopters on a `landHelipad` goal.
/// Two-phase like the runway path, but compressed: when the helo
/// gets within `HELIPAD_AUTO_ENGAGE_DISTANCE_NM` of the goal pad
/// while still en route, flip into landing (no player command
/// required — helipad ops self-engage in the real world too); each
/// subsequent tick steers toward the pad and watches for touchdown.
/// Returns a TouchdownInfo on touchdown (same shape as `tickLanding`
/// so GameSession can reuse `recordLanding`).
export function tickHelipadLanding(
  aircraft: Aircraft,
  airports: Airport[],
  pxPerNM: number,
  projector: CoordinateProjector,
): TouchdownInfo | null {
  const goal = aircraft.goal.landHelipad
  if (!goal) return null
  if (aircraft.type !== 'helicopter') return null
  if (aircraft.status !== 'enRoute' && aircraft.status !== 'landing') return null

  const airport = airports.find((a) => a.name === goal.airportName)
  if (!airport) return null
  const helipad = airport.helipads?.find((p) => p.id === goal.helipadId)
  if (!helipad) return null

  const padPx = projector.project(helipad.coordinate, true)
  const dx = padPx.x - aircraft.position.x
  const dy = padPx.y - aircraft.position.y
  const distPx = Math.hypot(dx, dy)
  const distNM = distPx / pxPerNM

  // Auto-engage. Only happens once — the next tick will see status
  // === 'landing' and skip this branch.
  if (aircraft.status === 'enRoute') {
    if (distNM > HELIPAD_AUTO_ENGAGE_DISTANCE_NM) return null
    const elevation = helipad.elevation ?? airport.elevation ?? 0
    aircraft.status = 'landing'
    aircraft.directToPoint = padPx
    aircraft.directToName = helipad.id
    aircraft.descentRateOverride = HELIPAD_DESCENT_RATE_FT_PER_MIN
    aircraft.targetAltitudeFt = elevation
    aircraft.targetSpeedKts = 0
    aircraft.holdingState = null
    return null
  }

  // status === 'landing' — steer toward the pad and watch for touchdown.
  // Skip if a runway landing has separately taken over (landingRunway
  // set). That can happen if the player issued a runway clearance to
  // a helo before the auto-engage fired; runway path wins.
  if (aircraft.landingRunway) return null

  aircraft.directToPoint = padPx
  aircraft.targetHeadingDeg = headingToward(aircraft.position, padPx)

  const elevation = helipad.elevation ?? airport.elevation ?? 0
  if (
    aircraft.altitudeFt <= elevation + TOUCHDOWN_ALTITUDE_MARGIN_FT &&
    distPx <= TOUCHDOWN_RADIUS_NM * pxPerNM
  ) {
    aircraft.status = 'landed'
    return { aircraftId: aircraft.id, airportName: airport.name }
  }
  return null
}

export interface ExitInfo {
  aircraftId: string
  /// Why the aircraft left the radar — for scoring + acknowledgement.
  /// `survey` aircraft hit this with their `exitWaypointName` after
  /// the hold expires; they count toward `handledSuccess` but earn
  /// no score bonus (survey traffic is "decoration").
  reason: 'transition' | 'departure' | 'survey'
  /// The waypoint the aircraft exited through (for goal evaluation).
  waypointName: string
}

/// Per-tick autopilot for transition aircraft and post-takeoff
/// aircraft. Both behaviors are: fly direct to the exit fix; when
/// within FIX_OVERFLY_RADIUS_NM, mark the aircraft completed and
/// return an ExitInfo so the session can score the success.
///
/// Also handles `survey` goals once the hold has expired — at that
/// point the aircraft is flying direct to `goal.survey.exitWaypointName`
/// and this function picks up the exit just like a transition.
export function tickFixExit(aircraft: Aircraft, pxPerNM: number): ExitInfo | null {
  if (aircraft.status === 'completed' || aircraft.status === 'landed') return null
  if (!aircraft.directToPoint || !aircraft.directToName) return null

  // Only transition / takeoff / survey-post-hold goals trigger the
  // exit; landing aircraft also have a directToPoint (the threshold)
  // which we must not eat. Survey aircraft only count as exiting once
  // they're on the way to the exit waypoint (post-hold) — while they
  // still target the circle waypoint they belong to tickSurvey.
  const reason: ExitInfo['reason'] | null = aircraft.goal.transition
    ? 'transition'
    : aircraft.goal.takeoff
      ? 'departure'
      : aircraft.goal.survey &&
          aircraft.directToName === aircraft.goal.survey.exitWaypointName
        ? 'survey'
        : null
  if (!reason) return null

  aircraft.targetHeadingDeg = headingToward(aircraft.position, aircraft.directToPoint)

  const dx = aircraft.directToPoint.x - aircraft.position.x
  const dy = aircraft.directToPoint.y - aircraft.position.y
  const distPx = Math.hypot(dx, dy)
  if (distPx > FIX_OVERFLY_RADIUS_NM * pxPerNM) return null

  // Aircraft has crossed its exit fix.
  const waypointName = aircraft.directToName
  aircraft.status = 'completed'
  aircraft.directToPoint = null
  aircraft.directToName = null
  // Survey aircraft are leaving via the auto-engaged hold's tail; the
  // hold itself was cleared when tickSurvey routed them to the exit.
  // Belt-and-braces clear in case external state got out of sync.
  aircraft.surveyHoldRemainingSec = null
  // Lock heading to whatever the aircraft is actually flying so it
  // continues outbound instead of pivoting back toward the fix that's
  // now behind it.
  aircraft.targetHeadingDeg = aircraft.headingDeg
  return { aircraftId: aircraft.id, reason, waypointName }
}

/// Per-tick autopilot for `survey` goal aircraft. Three sub-phases:
///   1. En route to the circle waypoint — directToName === circle.
///      When within FIX_OVERFLY_RADIUS_NM, engage a holding pattern
///      at the fix (right-turn racetrack, inbound = bearing-to-fix at
///      engagement time) and set `surveyHoldRemainingSec` to the
///      published hold duration. `hasScored: true` suppresses the
///      "Entered hold" bonus that a player-issued hold would earn —
///      survey hold is auto-engaged, not a controller skill move.
///   2. Holding — each frame, decrement the timer by the scaled
///      delta-time. tickHold owns the racetrack navigation.
///   3. Hold expired — clear `holdingState` and `surveyHoldRemainingSec`,
///      set directTo to the exit waypoint, status back to `enRoute`.
///      `tickFixExit` then picks up the survey aircraft and exits it
///      when it crosses the exit waypoint (with reason: 'survey').
///
/// Player overrides (setHeading, cancelHold, directTo elsewhere) are
/// expected to clear `surveyHoldRemainingSec` at the command site so
/// this tick exits cleanly without stomping on the player's intent.
///
/// Mirrors iOS GameViewModel.swift:1696-1762 (engagement + exit) +
/// 2318-2351 (hold-timer tick).
export function tickSurvey(
  aircraft: Aircraft,
  level: LevelData,
  projector: CoordinateProjector,
  pxPerNM: number,
  dtSec: number,
): { engaged: 'hold'; circleWaypointName: string } | { engaged: 'exit'; exitWaypointName: string } | null {
  const survey = aircraft.goal.survey
  if (!survey) return null
  if (aircraft.status === 'completed' || aircraft.status === 'landed') return null

  // Phase 2/3: hold timer is ticking.
  if (aircraft.surveyHoldRemainingSec != null) {
    aircraft.surveyHoldRemainingSec -= dtSec
    if (aircraft.surveyHoldRemainingSec > 0) return null
    // Phase 3: hold expired — route to the exit fix.
    const exitWp = level.waypoints.find((w) => w.name === survey.exitWaypointName)
    if (!exitWp) {
      // Unknown exit fix — clear hold state but leave the aircraft on
      // its current heading; the level-loader should reject this case
      // before runtime, so we treat it as a no-op recovery.
      aircraft.surveyHoldRemainingSec = null
      aircraft.holdingState = null
      return null
    }
    aircraft.holdingState = null
    aircraft.surveyHoldRemainingSec = null
    aircraft.directToPoint = projector.project(exitWp.coordinate, true)
    aircraft.directToName = exitWp.name
    aircraft.status = 'enRoute'
    return { engaged: 'exit', exitWaypointName: exitWp.name }
  }

  // Phase 1: en route to circle. Only engage when the aircraft is
  // actually targeting the circle waypoint (player may have vectored
  // them off — don't auto-engage in that case).
  if (
    aircraft.directToName !== survey.circleWaypointName ||
    !aircraft.directToPoint ||
    aircraft.holdingState
  ) {
    return null
  }
  const dx = aircraft.directToPoint.x - aircraft.position.x
  const dy = aircraft.directToPoint.y - aircraft.position.y
  if (Math.hypot(dx, dy) > FIX_OVERFLY_RADIUS_NM * pxPerNM) return null

  // Engage hold + start the survey timer.
  const circleWp = level.waypoints.find((w) => w.name === survey.circleWaypointName)
  if (!circleWp) return null
  const fixPoint = projector.project(circleWp.coordinate, true)
  // Same inbound-heading formula commandHold uses (CommandService:207-212).
  const fdx = fixPoint.x - aircraft.position.x
  const fdy = fixPoint.y - aircraft.position.y
  let inboundHeadingDeg = (Math.atan2(fdy, fdx) * 180) / Math.PI + 90
  if (inboundHeadingDeg < 0) inboundHeadingDeg += 360
  if (inboundHeadingDeg >= 360) inboundHeadingDeg -= 360
  aircraft.holdingState = {
    fixName: circleWp.name,
    fixPoint,
    inboundHeadingDeg,
    phase: 'flyingToWaypoint',
    outboundTimerSec: 0,
    hasScored: true, // suppress "Entered hold" bonus for auto-engaged survey hold
  }
  aircraft.surveyHoldRemainingSec = survey.holdDurationSec
  // Hand navigation to the hold autopilot.
  aircraft.directToPoint = null
  aircraft.directToName = null
  return { engaged: 'hold', circleWaypointName: circleWp.name }
}

/// Begin the takeoff roll on an awaiting-takeoff aircraft. Sets the
/// target speed to V2, marks status as `takingOff`, and tells the
/// autopilot what climbout target/altitude to use. Once the aircraft
/// reaches V2 speed (and is airborne), `tickClimbout` flips it to
/// `enRoute` and the regular tick takes over.
///
/// For SID-departure goals, also engages the flight plan: directTo
/// is set to the SID's first leg waypoint and the leg's altitude /
/// speed restrictions are applied as the climbout targets.
export function commandTakeoff(
  aircraft: Aircraft,
  level: LevelData,
  projector: CoordinateProjector,
): { ok: boolean; reason?: 'not_on_ground' | 'no_target_waypoint' } {
  if (aircraft.status !== 'awaitingTakeoff') {
    return { ok: false, reason: 'not_on_ground' }
  }

  // SID departure: pick the first leg's waypoint, set up the flight
  // plan, apply leg-1 restrictions for the climbout.
  if (aircraft.goal.departure) {
    const dep = aircraft.goal.departure
    const airport = level.airports.find((a) => a.name === dep.airportName)
    const sid = airport?.sids?.find(
      (s) => s.name === dep.sidName && s.runway === dep.runwayId,
    )
    const firstLeg = sid?.legs[0]
    if (!sid || !firstLeg) {
      return { ok: false, reason: 'no_target_waypoint' }
    }
    const targetWp = level.waypoints.find((w) => w.name === firstLeg.waypointName)
    if (!targetWp) return { ok: false, reason: 'no_target_waypoint' }

    aircraft.status = 'takingOff'
    aircraft.flightPlan = {
      procedureName: sid.name,
      legs: sid.legs,
      currentLegIndex: 0,
    }
    applyLegRestrictions(aircraft, firstLeg)
    aircraft.directToPoint = projector.project(targetWp.coordinate, true)
    aircraft.directToName = firstLeg.waypointName
    if (isDepartingFromCarrier(aircraft, level)) applyCatapultLaunch(aircraft)
    return { ok: true }
  }

  // Legacy `takeoff` goal — no flight plan, fly straight to the
  // single exit fix.
  const goal = aircraft.goal.takeoff
  if (!goal) return { ok: false, reason: 'no_target_waypoint' }
  const targetWp = level.waypoints.find((w) => w.name === goal.waypointName)
  if (!targetWp) return { ok: false, reason: 'no_target_waypoint' }

  const perf = performanceFor(aircraft.type)
  aircraft.status = 'takingOff'
  aircraft.targetSpeedKts = perf.v2Speed
  aircraft.targetAltitudeFt = TAKEOFF_TARGET_ALTITUDE_FT
  aircraft.directToPoint = projector.project(targetWp.coordinate, true)
  aircraft.directToName = targetWp.name
  if (isDepartingFromCarrier(aircraft, level)) applyCatapultLaunch(aircraft)
  return { ok: true }
}

/// True when the aircraft is awaiting takeoff at a carrier-flagged
/// airport. Carriers don't have ground roll — the catapult takes
/// the aircraft from 0 to V2 in seconds.
function isDepartingFromCarrier(aircraft: Aircraft, level: LevelData): boolean {
  const apName = aircraft.departureRunway?.airportName
  if (!apName) return false
  const ap = level.airports.find((a) => a.name === apName)
  return ap?.carrier === true
}

/// Catapult launch: skip the takeoff roll. Speed jumps directly
/// to V2; the next tickClimbout will see speed ≥ V2 and flip the
/// aircraft into enRoute. Faithful enough to a steam-cat shot for
/// the player — no ~30-second roll-out simulation.
function applyCatapultLaunch(aircraft: Aircraft): void {
  const perf = performanceFor(aircraft.type)
  aircraft.speedKts = perf.v2Speed
}

/// Apply a leg's altitude + speed restrictions to the aircraft's
/// targets (capped to the type's published max).
function applyLegRestrictions(aircraft: Aircraft, leg: ProcedureLeg): void {
  const perf = performanceFor(aircraft.type)
  if (leg.altitudeRestriction != null) {
    aircraft.targetAltitudeFt = Math.min(perf.maxAltitudeFt, leg.altitudeRestriction)
  } else {
    // No restriction on this leg — give the aircraft a sensible
    // climb target so it doesn't sit at 0 ft post-takeoff.
    if (aircraft.status === 'takingOff') {
      aircraft.targetAltitudeFt = TAKEOFF_TARGET_ALTITUDE_FT
    }
  }
  if (leg.speedRestriction != null) {
    aircraft.targetSpeedKts = Math.min(perf.maxSpeedKts, leg.speedRestriction)
  } else if (aircraft.status === 'takingOff') {
    aircraft.targetSpeedKts = perf.v2Speed
  }
}

export interface DirectToCrossingInfo {
  aircraftId: string
  fixName: string
}

/// Per-tick walker for SID + STAR flight plans surfaces this when
/// an aircraft crosses a leg waypoint. The session uses it to award
/// the procedure-violation penalty and update the breakdown row.
export interface FlightPlanLegEvent {
  aircraftId: string
  procedureName: string
  /// Waypoint name the aircraft just crossed.
  waypointName: string
  /// True when `altitudeFt` was outside the leg's `altitudeRestriction`
  /// at the moment of crossing. Tolerance: ±100 ft.
  altitudeViolation: boolean
  /// True when `speedKts` exceeded the leg's `speedRestriction` at
  /// the moment of crossing. Tolerance: +10 kt (we don't punish
  /// flying slower than the cap — that's pilot discretion).
  speedViolation: boolean
}

export interface FlightPlanCompletionEvent {
  aircraftId: string
  procedureName: string
  /// 'departure' = SID complete, aircraft exits airspace.
  /// 'arrival' = STAR complete, hand off to approach.
  kind: 'departure' | 'arrival'
  /// The procedure's last waypoint name — used by the session to
  /// score the exit (departure) or announce the approach handoff.
  lastWaypoint: string
}

const FLIGHT_PLAN_LEG_RADIUS_NM = 0.5

/// Per-tick walker for SID + STAR flight plans. Steers toward the
/// current leg's waypoint; on arrival, applies the next leg's
/// restrictions and advances. Final-leg arrival surfaces a
/// completion event the session uses to either exit (departure) or
/// hand off to approach (arrival).
export function tickFlightPlan(
  aircraft: Aircraft,
  level: LevelData,
  projector: CoordinateProjector,
  pxPerNM: number,
): { leg?: FlightPlanLegEvent; completed?: FlightPlanCompletionEvent } | null {
  const plan = aircraft.flightPlan
  if (!plan) return null
  if (
    aircraft.status === 'landing' ||
    aircraft.status === 'landed' ||
    aircraft.status === 'completed' ||
    aircraft.status === 'awaitingTakeoff'
  ) {
    return null
  }

  const currentLeg = plan.legs[plan.currentLegIndex]
  if (!currentLeg) {
    aircraft.flightPlan = null
    return null
  }
  const wp = level.waypoints.find((w) => w.name === currentLeg.waypointName)
  if (!wp) {
    // Bad data — abandon the plan and let the aircraft fly straight.
    aircraft.flightPlan = null
    return null
  }
  const wpPx = projector.project(wp.coordinate, true)
  aircraft.targetHeadingDeg = headingToward(aircraft.position, wpPx)

  const dx = wpPx.x - aircraft.position.x
  const dy = wpPx.y - aircraft.position.y
  const distNM = Math.hypot(dx, dy) / pxPerNM
  if (distNM > FLIGHT_PLAN_LEG_RADIUS_NM) return null

  // Reached the leg's waypoint. Check for altitude + speed busts.
  // Altitude restriction is treated as a ceiling: passing through
  // higher than restriction +100 ft = bust. We don't punish flying
  // below it — climbing aircraft cross early SID fixes well under
  // their assigned altitude during normal climbout. Speed
  // restriction is a cap: only over-speed (+10 kt tolerance) fires.
  const altitudeViolation =
    currentLeg.altitudeRestriction != null &&
    aircraft.altitudeFt > currentLeg.altitudeRestriction + 100
  const speedViolation =
    currentLeg.speedRestriction != null &&
    aircraft.speedKts > currentLeg.speedRestriction + 10
  if (altitudeViolation || speedViolation) aircraft.procedureViolations += 1

  const legEvent: FlightPlanLegEvent = {
    aircraftId: aircraft.id,
    procedureName: plan.procedureName,
    waypointName: currentLeg.waypointName,
    altitudeViolation,
    speedViolation,
  }

  plan.currentLegIndex += 1
  if (plan.currentLegIndex >= plan.legs.length) {
    // Procedure complete.
    const kind: 'departure' | 'arrival' = aircraft.goal.departure
      ? 'departure'
      : 'arrival'
    const completion: FlightPlanCompletionEvent = {
      aircraftId: aircraft.id,
      procedureName: plan.procedureName,
      kind,
      lastWaypoint: currentLeg.waypointName,
    }
    aircraft.flightPlan = null
    aircraft.directToPoint = null
    aircraft.directToName = null
    return { leg: legEvent, completed: completion }
  }

  // Apply the next leg's restrictions and update directTo.
  const nextLeg = plan.legs[plan.currentLegIndex]
  if (nextLeg) {
    applyLegRestrictions(aircraft, nextLeg)
    const nextWp = level.waypoints.find((w) => w.name === nextLeg.waypointName)
    if (nextWp) {
      aircraft.directToPoint = projector.project(nextWp.coordinate, true)
      aircraft.directToName = nextLeg.waypointName
    }
  }
  return { leg: legEvent }
}

/// Returns a crossing event the frame the aircraft passes the fix
/// so the session can announce "FOA101, crossing CARYN" via the
/// readback toast stack.
///
/// Only fires for `enRoute` aircraft — landing/transition/takeoff
/// have their own dedicated handlers that own the directTo slot.
export function tickDirectTo(
  aircraft: Aircraft,
  pxPerNM: number,
): DirectToCrossingInfo | null {
  if (aircraft.status !== 'enRoute') return null
  if (!aircraft.directToPoint) return null
  // Hold autopilot owns heading while a racetrack is active.
  if (aircraft.holdingState) return null
  // Flight plan owns navigation while a SID/STAR is engaged.
  if (aircraft.flightPlan) return null
  // Don't override goal-driven steering. tickFixExit already handles
  // transition/takeoff aircraft pointed at their exit fix.
  if (aircraft.goal.transition || aircraft.goal.takeoff) return null

  aircraft.targetHeadingDeg = headingToward(aircraft.position, aircraft.directToPoint)

  // Arrived → drop the directTo, aircraft continues on its current
  // heading. Player can re-issue if they want a new fix.
  const dx = aircraft.directToPoint.x - aircraft.position.x
  const dy = aircraft.directToPoint.y - aircraft.position.y
  const distNM = Math.hypot(dx, dy) / pxPerNM
  if (distNM <= 0.2) {
    const crossedName = aircraft.directToName
    aircraft.directToPoint = null
    aircraft.directToName = null
    return crossedName ? { aircraftId: aircraft.id, fixName: crossedName } : null
  }
  return null
}

export type ApproachRejection =
  | 'not_aligned'
  | 'too_far'
  | 'speed_zero'
  | 'invalid_runway'
  | 'already_landing'
  | 'past_threshold'

export interface ApproachEvaluation {
  ok: boolean
  reason?: ApproachRejection
  headingOffsetDeg: number
  distanceNM: number
  thresholdPx: { x: number; y: number }
  approachHeadingDeg: number
  airport: Airport
  runway: Runway
}

/// Pure evaluation of "would Cleared for approach succeed right
/// now?" — used by the chrome to enable/disable the contextual
/// button. Looser than landing: 45° tolerance, 15 NM max.
export function evaluateApproachClearance(args: {
  aircraft: Aircraft
  airport: Airport
  runway: Runway
  approachHeadingDeg: number
  projector: CoordinateProjector
  pxPerNM: number
}): ApproachEvaluation {
  const { aircraft, airport, runway, approachHeadingDeg, projector, pxPerNM } = args

  const thresholdGeo =
    approachHeadingDeg === runway.heading1
      ? runway.startCoordinate
      : runway.endCoordinate
  const thresholdPx = projector.project(thresholdGeo, true)
  const dx = aircraft.position.x - thresholdPx.x
  const dy = aircraft.position.y - thresholdPx.y
  const distanceNM = Math.hypot(dx, dy) / pxPerNM

  let headingOffsetDeg = Math.abs(aircraft.headingDeg - approachHeadingDeg)
  if (headingOffsetDeg > 180) headingOffsetDeg = 360 - headingOffsetDeg

  const base = {
    headingOffsetDeg,
    distanceNM,
    thresholdPx,
    approachHeadingDeg,
    airport,
    runway,
  }

  if (aircraft.status === 'landing' || aircraft.status === 'landed') {
    return { ok: false, reason: 'already_landing', ...base }
  }
  if (aircraft.speedKts <= 0) return { ok: false, reason: 'speed_zero', ...base }
  if (headingOffsetDeg > APPROACH_INTERCEPT_MAX_OFFSET_DEG) {
    return { ok: false, reason: 'not_aligned', ...base }
  }
  if (distanceNM > APPROACH_INTERCEPT_MAX_DISTANCE_NM) {
    return { ok: false, reason: 'too_far', ...base }
  }
  // Half-plane check: aircraft must be on the OUTBOUND side of the
  // threshold (the side the localizer extends to). Aircraft that
  // have flown past the deck are geometrically valid as "near the
  // runway" but the autopilot can't establish them on a half-line
  // from the wrong side. Reject so the player vectors them around.
  const bearingFromThreshold = headingToward(thresholdPx, aircraft.position)
  const expectedOutbound = (approachHeadingDeg + 180) % 360
  let halfPlaneDelta = bearingFromThreshold - expectedOutbound
  while (halfPlaneDelta > 180) halfPlaneDelta -= 360
  while (halfPlaneDelta < -180) halfPlaneDelta += 360
  if (Math.abs(halfPlaneDelta) > 90) {
    return { ok: false, reason: 'past_threshold', ...base }
  }
  return { ok: true, ...base }
}

export interface ApproachClearanceResult {
  ok: boolean
  reason?: ApproachRejection
}

/// Issue a Cleared-for-approach. On accept, sets
/// `aircraft.approachIntercept` so `tickApproachIntercept` will
/// drive the heading every frame. Altitude stays under player
/// control — the player issues Cleared-to-land separately to
/// commit to the descent.
export function issueApproachClearance(args: {
  aircraft: Aircraft
  airport: Airport
  runway: Runway
  approachHeadingDeg: number
  projector: CoordinateProjector
  pxPerNM: number
}): ApproachClearanceResult {
  const evalResult = evaluateApproachClearance(args)
  if (!evalResult.ok) return { ok: false, reason: evalResult.reason }
  const { aircraft, airport, runway } = args
  aircraft.approachIntercept = {
    airportName: airport.name,
    runwayId1: runway.id1,
    runwayId2: runway.id2,
    activeRunwayId:
      evalResult.approachHeadingDeg === runway.heading1 ? runway.id1 : runway.id2,
    approachHeadingDeg: evalResult.approachHeadingDeg,
    thresholdPx: evalResult.thresholdPx,
    established: false,
  }
  // Drop any active player directTo — the autopilot owns heading now.
  aircraft.directToPoint = null
  aircraft.directToName = null
  return { ok: true }
}

export interface EstablishedInfo {
  aircraftId: string
  /// Whichever runway end the approach was issued against (heading1
  /// vs heading2, picked at clearance time).
  runwayId: string
}

/// Per-tick: while approachIntercept is active, drive
/// targetHeadingDeg either toward the runway (when off-localizer)
/// or aligned with it (when established). Returns an
/// `EstablishedInfo` the frame the aircraft first reaches the
/// on-localizer band so the session can announce "FOA101,
/// established RWY 22".
export function tickApproachIntercept(
  aircraft: Aircraft,
  pxPerNM: number,
  airports?: Airport[],
  projector?: CoordinateProjector,
): EstablishedInfo | null {
  const ctx = aircraft.approachIntercept
  if (!ctx) return null
  // Landing autopilot takes over once the player issues Cleared to
  // land — `tickLanding` already runs first in the session loop.
  if (
    aircraft.status === 'landing' ||
    aircraft.status === 'landed' ||
    aircraft.status === 'completed' ||
    aircraft.status === 'awaitingTakeoff'
  ) {
    return null
  }

  // Re-project the threshold each tick from the live runway
  // endpoint. Static airports: the projected coords don't change,
  // so this is a no-op. Moving carriers: the cached
  // `ctx.thresholdPx` would otherwise drift away from the deck —
  // refresh it so the autopilot tracks the live position.
  // `airports` + `projector` are optional so existing unit tests
  // that hand-build a context with a fixed thresholdPx don't need
  // to plumb them through; production callers always supply both.
  if (airports && projector) {
    const ap = airports.find((a) => a.name === ctx.airportName)
    const runway = ap?.runways.find(
      (r) => r.id1 === ctx.runwayId1 && r.id2 === ctx.runwayId2,
    )
    if (runway) {
      const thresholdGeo =
        ctx.approachHeadingDeg === runway.heading1
          ? runway.startCoordinate
          : runway.endCoordinate
      ctx.thresholdPx = projector.project(thresholdGeo, true)
    }
  }

  // Perpendicular offset from the runway extended centerline. The
  // bearing from the threshold to the aircraft should equal the
  // centerline-outbound direction (approachHeading + 180); any
  // delta is the angular displacement, which we project to a
  // signed perpendicular distance via sin.
  //
  // perpNM > 0 ⟹ aircraft is on the pilot's LEFT of the localizer
  // (counter-intuitive but follows from sin(delta) being positive
  // when delta is positive: a positive bearing-delta means the
  // aircraft is clockwise of the outbound centerline direction,
  // which when you face the landing direction sits behind your
  // left shoulder).
  // perpNM < 0 ⟹ aircraft is on the pilot's RIGHT of the localizer.
  const dx = aircraft.position.x - ctx.thresholdPx.x
  const dy = aircraft.position.y - ctx.thresholdPx.y
  const distPx = Math.hypot(dx, dy)
  const distNM = distPx / pxPerNM
  const bearing = headingToward(ctx.thresholdPx, aircraft.position)
  const expected = (ctx.approachHeadingDeg + 180) % 360
  let delta = bearing - expected
  while (delta > 180) delta -= 360
  while (delta < -180) delta += 360
  const perpNM = Math.sin((delta * Math.PI) / 180) * distNM

  // Half-plane gate: the localizer is a HALF-line that extends
  // outward from the threshold opposite the landing direction.
  // An aircraft past the threshold (in the deck-forward half-plane)
  // has bearing-from-threshold ≈ approachHeading (delta near ±180);
  // sin(±180°) is zero, which would otherwise spoof "on localizer".
  // Treat that side as off-localizer and steer back toward the
  // threshold so the aircraft can re-enter the correct half-plane.
  const onOutboundSide = Math.abs(delta) < 90

  // On the localizer ⟹ track runway heading. First time we cross
  // into this branch, flip the established flag and surface the
  // event so the session can announce it.
  if (onOutboundSide && Math.abs(perpNM) < 0.3) {
    aircraft.targetHeadingDeg = ctx.approachHeadingDeg
    if (!ctx.established) {
      ctx.established = true
      return { aircraftId: aircraft.id, runwayId: ctx.activeRunwayId }
    }
    return null
  }

  // If the aircraft has flown past the threshold, the "intercept
  // angle" formula doesn't help — we need to turn back toward the
  // threshold so the aircraft can re-enter the localizer's
  // outbound half-plane. Steer directly at the threshold; the
  // player retains the option to cancel via direct-to or a manual
  // heading.
  if (!onOutboundSide) {
    aircraft.targetHeadingDeg = headingToward(aircraft.position, ctx.thresholdPx)
    return null
  }

  // Off the localizer ⟹ intercept at up to 45°, scaled by offset
  // (gentler turn when nearly on; sharper when far off).
  // Pilot's-left offset (perpNM > 0) ⟹ turn RIGHT to close
  // (target = approach + Δ); pilot's-right offset (perpNM < 0)
  // ⟹ turn LEFT (target = approach − Δ).
  const interceptAngle = Math.min(45, Math.abs(perpNM) * 30)
  const sign = perpNM > 0 ? 1 : -1
  let target = ctx.approachHeadingDeg + sign * interceptAngle
  target = ((target % 360) + 360) % 360
  aircraft.targetHeadingDeg = target
  return null
}

const HOLD_FIX_ARRIVAL_RADIUS_NM = 0.2
const HOLD_OUTBOUND_LEG_SEC = 60
const HOLD_TURN_RATE_DEG_PER_SEC = 3
/// Score awarded the first time an aircraft reaches its hold fix —
/// matches iOS's +100 (GameViewModel.swift:2220-2225).
export const HOLD_ENTER_SCORE = 100

export interface HoldEnterInfo {
  aircraftId: string
  fixName: string
}

/// Per-tick autopilot for an aircraft in a racetrack hold. Drives
/// targetHeadingDeg through the four-phase cycle — and during the
/// two turn phases drives `headingDeg` directly at the standard rate
/// to force a right-hand turn (the underlying advanceHeading picks
/// the *shortest* direction, which is ambiguous for a 180° turn).
///
/// Returns a `HoldEnterInfo` the first frame the aircraft arrives at
/// the fix so the session can score the entry bonus.
export function tickHold(
  aircraft: Aircraft,
  pxPerNM: number,
  dt: number,
): HoldEnterInfo | null {
  const state = aircraft.holdingState
  if (!state) return null
  if (
    aircraft.status === 'landing' ||
    aircraft.status === 'landed' ||
    aircraft.status === 'completed' ||
    aircraft.status === 'awaitingTakeoff'
  ) {
    return null
  }

  let entered: HoldEnterInfo | null = null

  switch (state.phase) {
    case 'flyingToWaypoint': {
      // Steer toward the fix; check arrival.
      aircraft.targetHeadingDeg = headingToward(aircraft.position, state.fixPoint)
      const dx = state.fixPoint.x - aircraft.position.x
      const dy = state.fixPoint.y - aircraft.position.y
      const distNM = Math.hypot(dx, dy) / pxPerNM
      if (distNM < HOLD_FIX_ARRIVAL_RADIUS_NM) {
        if (!state.hasScored) {
          state.hasScored = true
          entered = { aircraftId: aircraft.id, fixName: state.fixName }
        }
        state.phase = 'firstTurn'
      }
      break
    }
    case 'firstTurn': {
      // 180° right turn from inbound to outbound.
      const outbound = (state.inboundHeadingDeg + 180) % 360
      tickRightTurn(aircraft, outbound, dt)
      if (aircraft.headingDeg === outbound) {
        state.phase = 'outboundLeg'
        state.outboundTimerSec = 0
      }
      break
    }
    case 'outboundLeg': {
      const outbound = (state.inboundHeadingDeg + 180) % 360
      aircraft.targetHeadingDeg = outbound
      state.outboundTimerSec += dt
      if (state.outboundTimerSec >= HOLD_OUTBOUND_LEG_SEC) {
        state.phase = 'secondTurn'
      }
      break
    }
    case 'secondTurn': {
      // 180° right turn from outbound back to inbound.
      tickRightTurn(aircraft, state.inboundHeadingDeg, dt)
      if (aircraft.headingDeg === state.inboundHeadingDeg) {
        state.phase = 'flyingToWaypoint'
      }
      break
    }
  }

  return entered
}

/// Force a right turn from current heading to `targetDeg` at the
/// standard 3°/s rate. Drives `headingDeg` directly (and keeps
/// `targetHeadingDeg` in sync so advanceHeading is a no-op for this
/// aircraft this tick) — matches iOS's `commandedTurnDirection = .right`
/// behavior without needing that field on the web aircraft model.
function tickRightTurn(aircraft: Aircraft, targetDeg: number, dt: number): void {
  const step = HOLD_TURN_RATE_DEG_PER_SEC * dt
  // Right-turn (CW) angular distance still to cover.
  let remaining = (targetDeg - aircraft.headingDeg + 360) % 360
  if (remaining < step || remaining < 0.5) {
    aircraft.headingDeg = targetDeg
  } else {
    aircraft.headingDeg = (aircraft.headingDeg + step) % 360
  }
  aircraft.targetHeadingDeg = aircraft.headingDeg
}

const GO_AROUND_AGL_FT = 4000

export interface GoAroundResult {
  ok: boolean
  reason?: 'not_on_approach'
}

/// Abort a landing in progress: clear the directTo target, drop
/// the descent override, and command a climb to ~4,000 ft above
/// field elevation. The aircraft reverts to `enRoute` and continues
/// on its current heading — the player can then vector it for
/// another approach.
///
/// MVP simplification: we don't have published missed-approach
/// procedures (no SID/STAR lookup yet), so the climb is a flat
/// "field elevation + 4000 ft" — clear of terrain on most levels,
/// short of MSA on the highest mountain levels (player can re-issue
/// altitude command for those).
export function commandGoAround(
  aircraft: Aircraft,
  airports: Airport[],
): GoAroundResult {
  if (aircraft.status !== 'landing') {
    return { ok: false, reason: 'not_on_approach' }
  }
  const airport = aircraft.landingRunway
    ? airports.find((a) =>
        a.runways.some(
          (r) =>
            r.id1 === aircraft.landingRunway?.id1 &&
            r.id2 === aircraft.landingRunway?.id2,
        ),
      )
    : null
  const fieldElev = airport?.elevation ?? 0
  aircraft.status = 'enRoute'
  aircraft.landingRunway = null
  aircraft.descentRateOverride = null
  aircraft.directToPoint = null
  aircraft.directToName = null
  aircraft.targetAltitudeFt = fieldElev + GO_AROUND_AGL_FT
  // targetHeadingDeg keeps the current heading — autopilot's
  // landing steer-toward-threshold has stopped, so the aircraft
  // continues past the runway on its current course until the
  // player vectors it.
  return { ok: true }
}

/// Per-tick handler for aircraft in the takeoff sequence. Once the
/// aircraft is past rotation speed AND airborne (alt ≥ field+50ft),
/// promote it to `enRoute` so the rest of the sim treats it normally.
/// Until then it just rolls/climbs straight on its current heading.
export function tickClimbout(aircraft: Aircraft, airports: Airport[]): void {
  if (aircraft.status !== 'takingOff') return
  const perf = performanceFor(aircraft.type)
  const departure = aircraft.departureRunway
  const airport = departure
    ? airports.find((a) => a.name === departure.airportName)
    : null
  const elevation = airport?.elevation ?? 0
  const airborne = aircraft.altitudeFt > elevation + 50
  const aboveRotationSpeed = aircraft.speedKts >= perf.rotationSpeed
  if (aboveRotationSpeed && airborne) {
    aircraft.status = 'enRoute'
  }
}

/// Convert a screen-Y-down vector into a magnetic heading.
function headingToward(from: { x: number; y: number }, to: { x: number; y: number }): number {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const screenAngle = Math.atan2(dy, dx)
  let deg = (screenAngle * 180) / Math.PI + 90
  if (deg < 0) deg += 360
  if (deg >= 360) deg -= 360
  return deg
}
